import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import PageSizeDropDownSelection from "../../components/CustomComponents/DropdownSelections/DropDownSelections.jsx";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector.jsx";
import Select from "react-select";
import { logout } from "../../app/auth/authSlice.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import AddUpdateShiftSlotModal from "./addUpdateShiftSlotModal.jsx";
import ViewShiftBookingsModal from "./viewShiftBookingsModal.jsx";
import swal from "sweetalert";
import "../compnay/ManageCompany.css";

const ManageRiderShiftSlots = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = useMemo(() => {
    const base = isDarkMode ? DropDownStylesDark : DropDownStyles;
    return {
      ...base,
      menu: (provided) => ({
        ...base.menu(provided),
        zIndex: 11000,
      }),
      menuPortal: (provided) => ({
        ...provided,
        zIndex: 11000,
      }),
    };
  }, [isDarkMode]);

  const [filterCountry, setFilterCountry] = useState("PAK");
  const [serviceCity, setServiceCity] = useState("");
  const [zoneFilter, setZoneFilter] = useState(null);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [slotToEdit, setSlotToEdit] = useState(null);
  const [bookingsSlot, setBookingsSlot] = useState(null);

  const loadZoneOptions = useCallback(async (countryCode, city) => {
    if (!countryCode || !city) {
      setZoneOptions([]);
      return;
    }
    try {
      const response = await axiosInstance.get(APIs.GET_MARKETPLACE_ZONES, {
        params: {
          CountryCode: countryCode,
          ServiceCity: city,
          IsActive: true,
          PageNumber: 1,
          PageSize: 200,
        },
      });
      if (response.data?.status) {
        const rows = Array.isArray(response.data.data) ? response.data.data : [];
        setZoneOptions(
          rows.map((z) => ({
            value: z.zoneId || z.Zone_ID,
            label: z.zoneName || z.ZoneName,
          }))
        );
      } else {
        setZoneOptions([]);
      }
    } catch {
      setZoneOptions([]);
    }
  }, []);

  useEffect(() => {
    loadZoneOptions(filterCountry, serviceCity);
    setZoneFilter(null);
  }, [filterCountry, serviceCity, loadZoneOptions]);

  const fetchSlots = useCallback(async () => {
    try {
      setLoading(true);
      const params = { PageNumber: pageNumber, PageSize: pageSize.value };
      if (serviceCity.trim()) params.ServiceCity = serviceCity.trim();
      if (zoneFilter?.value) params.Zone_ID = zoneFilter.value;
      if (fromDate) params.FromDate = fromDate;
      if (toDate) params.ToDate = toDate;

      const response = await axiosInstance.get(APIs.GET_RIDER_SHIFT_SLOTS, { params });
      if (response.data.status) {
        setSlots(Array.isArray(response.data.data) ? response.data.data : []);
        setTotalRecords(response.data.totalRecords || 0);
        setTotalPages(response.data.totalPages || 1);
      } else {
        showErrorToast(response.data.message || "Failed to fetch shift slots");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        dispatch(logout());
        setTimeout(() => navigate("/user/login", { replace: true }), 1000);
      } else {
        showErrorToast("Failed to fetch shift slots");
      }
    } finally {
      setLoading(false);
    }
  }, [serviceCity, zoneFilter?.value, fromDate, toDate, pageNumber, pageSize.value, dispatch, navigate]);

  useEffect(() => {
    const handler = setTimeout(() => fetchSlots(), 300);
    return () => clearTimeout(handler);
  }, [fetchSlots]);

  useEffect(() => {
    setPageNumber(1);
  }, [serviceCity, zoneFilter?.value, fromDate, toDate, pageSize.value]);

  const handleUpsert = async (payload) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_UPSERT_RIDER_SHIFT_SLOT, payload);
      if (response.data.status) {
        showSuccessToast(response.data.message || "Shift slot saved");
        setShowAddModal(false);
        setShowEditModal(false);
        setSlotToEdit(null);
        fetchSlots();
      } else {
        showErrorToast(response.data.message || "Failed to save shift slot");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save shift slot");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (shiftSlotId) => {
    const confirm = await swal({
      title: "Delete shift slot?",
      text: "If riders are booked, the slot will be deactivated instead.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirm) return;
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_DELETE_RIDER_SHIFT_SLOT, null, {
        params: { ShiftSlot_ID: shiftSlotId },
      });
      if (response.data.status) {
        showSuccessToast(response.data.message || "Shift slot removed");
        fetchSlots();
      } else {
        showErrorToast(response.data.message || "Failed to delete");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterCountry("PAK");
    setServiceCity("");
    setZoneFilter(null);
    setFromDate("");
    setToDate("");
  };

  const filterInputStyle = {
    backgroundColor: isDarkMode ? "rgba(45, 52, 54, 0.9)" : "rgba(255, 255, 255, 0.9)",
    border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid rgba(255, 255, 255, 0.3)",
    color: isDarkMode ? "#ffffff" : "#333",
    borderRadius: "6px",
    height: "36px",
  };

  const formatSlotDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
  };

  return (
    <div>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#/">Home</a></li>
            <li className="breadcrumb-item active">Rider Shift Slots</li>
          </ol>
          <h1 className="page-header mb-0">Rider Shift Slots</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            onClick={() => setShowAddModal(true)}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Shift Slot
          </button>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active">
            <div className="mb-4">
              <div
                className="card filter-card"
                style={{
                  position: "relative",
                  zIndex: 1,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  borderRadius: "12px",
                  border: "none",
                  boxShadow: `0 4px 20px ${themeColor}40`,
                }}
              >
                <div className="card-body p-3">
                  <div className="row g-3 align-items-end">
                    <CountryCitySelector
                      selectedCountry={filterCountry}
                      selectedCity={serviceCity}
                      setSelectedCity={setServiceCity}
                      onChange={(payload) => {
                        if (payload.CompCountry !== undefined) setFilterCountry(payload.CompCountry || "PAK");
                        if (payload.CompCity !== undefined) setServiceCity(payload.CompCity || "");
                      }}
                      selectedValueIsLabel={true}
                      isDarkMode={isDarkMode}
                    />
                    <div className="col-md-3">
                      <label className="form-label text-white mb-1">Zone</label>
                      <Select
                        value={zoneFilter}
                        onChange={setZoneFilter}
                        options={zoneOptions}
                        styles={dropdownStyles}
                        placeholder={serviceCity ? "All zones" : "Select city first"}
                        isClearable
                        isDisabled={!serviceCity}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </div>
                    <div className="col-xl-2 col-lg-2 col-md-6">
                      <input
                        type="date"
                        className="filter-input form-control"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        title="From date"
                        style={filterInputStyle}
                      />
                    </div>
                    <div className="col-xl-2 col-lg-2 col-md-6">
                      <input
                        type="date"
                        className="filter-input form-control"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        title="To date"
                        style={filterInputStyle}
                      />
                    </div>
                    <div className="col-xl-1 col-lg-1 col-md-6">
                      <div className="filter-control">
                        <PageSizeDropDownSelection
                          defaultValue={pageSize}
                          onChange={setPageSize}
                          styles={dropdownStyles}
                        />
                      </div>
                    </div>
                    <div className="col-xl-2 col-lg-2 col-md-12">
                      <button
                        type="button"
                        className="btn"
                        onClick={clearFilters}
                        title="Clear all filters"
                        style={{
                          height: "36px",
                          backgroundColor: isDarkMode ? "rgba(45, 52, 54, 0.8)" : "rgba(255, 255, 255, 0.2)",
                          border: "1px solid rgba(255, 255, 255, 0.3)",
                          color: "white",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        <i className="fa fa-times me-1"></i>
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="table-responsive table-container"
              style={{ minHeight: "200px", maxHeight: "450px", overflowY: "auto", overflowX: "auto", position: "relative", zIndex: 1 }}
            >
              <table className="table table-hover text-nowrap">
                <thead
                  className="sticky-header"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10000,
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <tr>
                    <th className="pt-2 pb-2">Date</th>
                    <th className="pt-2 pb-2">City</th>
                    <th className="pt-2 pb-2">Zone</th>
                    <th className="pt-2 pb-2">Time</th>
                    <th className="pt-2 pb-2">Capacity</th>
                    <th className="pt-2 pb-2">Booked</th>
                    <th className="pt-2 pb-2">Active</th>
                    <th className="pt-2 pb-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {slots.length > 0 ? (
                    slots.map((slot) => {
                      const id = slot.shiftSlotId || slot.ShiftSlot_ID;
                      const isActive = slot.isActive ?? slot.IsActive;
                      return (
                        <tr key={id}>
                          <td className="align-middle">{formatSlotDate(slot.shiftDate || slot.ShiftDate)}</td>
                          <td className="align-middle">{slot.serviceCity || slot.ServiceCity}</td>
                          <td className="align-middle">{slot.zoneName || slot.ZoneName || "—"}</td>
                          <td className="align-middle">{(slot.startTime || slot.StartTime) + " - " + (slot.endTime || slot.EndTime)}</td>
                          <td className="align-middle">{slot.maxRiders ?? slot.MaxRiders}</td>
                          <td className="align-middle">{slot.bookedCount ?? slot.BookedCount}</td>
                          <td className="align-middle">
                            <span className={`badge ${isActive ? "bg-success" : "bg-secondary"}`}>
                              {isActive ? "Yes" : "No"}
                            </span>
                          </td>
                          <td className="align-middle text-center">
                            <div className="d-flex justify-content-center gap-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => { setSlotToEdit(slot); setShowEditModal(true); }}
                                title="Edit shift slot"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "6px",
                                  border: `1px solid ${themeColor}`,
                                  color: themeColor,
                                }}
                              >
                                <i className="fa fa-edit" style={{ fontSize: "12px" }} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setBookingsSlot(slot)}
                                title="View bookings"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "6px",
                                }}
                              >
                                <i className="fa fa-users" style={{ fontSize: "12px" }} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(id)}
                                title="Delete shift slot"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "6px",
                                  border: "1px solid #dc3545",
                                  color: "#dc3545",
                                }}
                              >
                                <i className="fa fa-trash" style={{ fontSize: "12px" }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={8} className="align-middle text-center" style={{ fontSize: "18px" }}>
                        {loading ? "Loading shift slots..." : "No shift slots found."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalRecords={totalRecords}
              pageSize={parseInt(pageSize.value, 10) || 50}
              pageNumber={pageNumber}
              totalPages={totalPages}
              setPageNumber={setPageNumber}
              themeColor={themeColor}
            />
          </div>
        </div>
      </div>

      <AddUpdateShiftSlotModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onSubmit={handleUpsert} />
      <AddUpdateShiftSlotModal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSlotToEdit(null); }} onSubmit={handleUpsert} slot={slotToEdit} />
      <ViewShiftBookingsModal isOpen={!!bookingsSlot} onClose={() => setBookingsSlot(null)} slot={bookingsSlot} onChanged={fetchSlots} />
    </div>
  );
};

export default ManageRiderShiftSlots;
